/**
 * Petition signers (/dashboard/admin/signers): names and addresses from nominating petitions, for Chicago Politics staff
 * only (worker/src/petition-signers.js; GET /api/dashboard/admin/signers, …/map, …/export.csv). Built from the Alerts
 * page's parts (cards, members tables, chips); the map is the signer map's stack (SignerMap.tsx): Mapbox GL with the
 * staff-only public token (Light, Streets, Dark, Satellite), else the open basemap and a notice naming why.
 *
 *   Filters   candidate and ward (and a name / address search for the list)
 *   Map       hotspots per candidate: hundred-blocks (circles at the block's centroid, sized by signers), or a precinct
 *             choropleth. Never an address point.
 *   List      sheet, line, name and address as written, the matched address, precinct, legibility, struck
 *   Export    CSV of the full filtered list, staff owners and admins only; the server records every export first
 *   OCR       the OCR pipeline's status (…/ocr, worker-petition-ocr): pages queued, done, flagged and dead per petition
 *   Drive     the Drive pipeline's status (…/drive, worker-petition-pipeline): files seen, new or updated, downloaded,
 *             pages rendered and read, files a person must map, errors
 *   Counts    signature counts per petition (…/counts): sheets, signed lines, struck, net, the office's minimum and the lines
 *             transcribed so far; sortable, with totals, and a CSV of the counts only (…/counts.csv)
 *   Likely    the "Likely valid" column and its detail view (SignerValidity.tsx, …/validity): totals only
 *
 * The page fetches its own data. Nothing from it is cached in the browser.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { List, ListRow, RowSub, RowTitle } from "../components/shared/List";
import { Cell, Head } from "./Organizations";
import { BASEMAPS, BasemapSwitch, MapNotice, bboxOf, styleFor, useGL, useMapConfig, watchRejected } from "./SignerMap";
import type { Basemap, GLib } from "./SignerMap";
import { LikelyCell, ValidityControls, ValidityDetail, useValidity } from "./SignerValidity";

export interface SignerRow {
  id: number; candidate: string; office: string; ward: number | null; district: number | null; sheet: number | null; page: number; line: number; name: string; address: string;
  normalized: string | null; match: "exact" | "block" | "none"; block: string | null; precinct: number | null;
  legibility: "clear" | "partial" | "illegible"; confidence: number; struck: boolean; source: string;
}
export interface SignerCandidate { slug: string; office: string; ward: number | null; district: number | null; rows: number; signed: number; matched: number; loadedAt: string }
export interface SignersPage {
  filter: { candidate: string | null; ward: number | null; office: string | null; q: string | null }; total: number; offset: number; pageSize: number;
  rows: SignerRow[]; candidates: SignerCandidate[]; canExport: boolean;
  /** Candidates with OCR lines not yet reviewed (counts only). */
  staged?: StagedCandidate[];
}
export interface StagedCandidate { slug: string; name: string | null; office: string; ward: number | null; district: number | null; keys: string[]; rows: number; signed: number;
  geocoded: number; exact: number; fuzzy: number; none: number; inside: number; outside: number; unmatched?: boolean }
type Block = { candidate: string; block: string; lat: number; lon: number; n: number; staged?: boolean };
type Precinct = { candidate: string; ward: number; precinct: number; n: number };
export interface SignersMap {
  blocks: Block[];
  precincts: Precinct[];
  shapes: { ward: number; precinct: number; geometry: object }[];
  /** Not yet reviewed (OCR) lines, for a candidate or ward only; needsFilter when neither is picked. */
  staged?: { blocks: Block[]; precincts: Precinct[]; rows: number; needsFilter: boolean };
}
/** The Hotspots data with the not-yet-reviewed lines added (blocks flagged staged; precinct counts summed) or not. */
export function withStaged(data: SignersMap, on: boolean): SignersMap {
  if (!on || !data.staged) return data;
  const by = new Map(data.precincts.map((p) => [`${p.candidate}|${p.ward}-${p.precinct}`, { ...p }]));
  for (const p of data.staged.precincts) {
    const k = `${p.candidate}|${p.ward}-${p.precinct}`;
    const cur = by.get(k);
    if (cur) cur.n += p.n; else by.set(k, { ...p });
  }
  return { ...data, blocks: [...data.blocks, ...data.staged.blocks.map((b) => ({ ...b, staged: true }))], precincts: [...by.values()] };
}

export interface CountRow {
  candidate: string; name: string; office: string; ward: number | null; district: number | null; status: "counted" | "pending";
  countSource: "pages" | "candidate" | null; sheets: number | null; countedSheets: number | null; duplicateSheets: number;
  signed: number | null; struck: number | null; net: number | null; required: number | null; transcribed: number; source: string; countedOn: string | null;
}
export interface CountsPage {
  rows: CountRow[]; loadedAt: string | null;
  totals: { petitions: number; counted: number; pending: number; sheets: number; signed: number; struck: number; net: number; transcribed: number };
}
export type CountKey = "name" | "office" | "ward" | "sheets" | "signed" | "struck" | "net" | "required" | "transcribed";
const OFFICE_ORDER = ["mayor", "clerk", "treasurer", "alderperson", "police_district_council"];
/** Sorted copy: numbers by value, pending (no count) always last; office by the ballot's order, then ward or district. */
export function sortCounts(rows: CountRow[], key: CountKey, dir: 1 | -1): CountRow[] {
  const val = (r: CountRow): number | string | null => key === "name" ? r.name.toLowerCase()
    : key === "office" ? OFFICE_ORDER.indexOf(r.office) * 1000 + (r.ward ?? r.district ?? 0)
    : key === "ward" ? r.ward ?? r.district ?? null : r[key];
  return [...rows].sort((a, b) => {
    const x = val(a), y = val(b);
    if (x == null || y == null) return x == null && y == null ? a.name.localeCompare(b.name) : x == null ? 1 : -1;
    return (x < y ? -1 : x > y ? 1 : a.name.localeCompare(b.name)) * dir;
  });
}

const API = "/api/dashboard/admin/signers";
const COLORS = ["#2563eb", "#dc2626", "#059669", "#d97706", "#7c3aed", "#db2777"];
const count = (n: number) => n.toLocaleString("en-US");
const pct = (a: number, b: number) => (b ? `${Math.round((100 * a) / b)}%` : "–");
const OFFICE: Record<string, string> = { mayor: "Mayor", clerk: "City Clerk", treasurer: "City Treasurer", police_district_council: "Police District Council" };
/** "Ward 49", "City Clerk", "Police District Council 24". */
export const raceOf = (c: { office: string; ward: number | null; district: number | null }) =>
  c.office === "alderperson" ? `Ward ${c.ward}` : `${OFFICE[c.office] ?? c.office}${c.district ? ` ${c.district}` : ""}`;
const titleOf = (slug: string) => slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

/** The query string for a filter (the list, the map and the export use the same one). */
export function signerQuery(f: { candidate?: string | null; ward?: number | null; q?: string | null; offset?: number }): string {
  const p = new URLSearchParams();
  if (f.candidate) p.set("candidate", f.candidate);
  if (f.ward) p.set("ward", String(f.ward));
  if (f.q) p.set("q", f.q);
  if (f.offset) p.set("offset", String(f.offset));
  const s = p.toString();
  return s ? `?${s}` : "";
}

// The Hotspots map: the signer map's stack (SignerMap.tsx: Mapbox GL with the staff-only public token, else the open
// basemap and a notice). The GeoJSON is built here and stays in the browser: each block or precinct carries an index, a
// candidate index (its colour) and a count, never a name or an address; labels are looked up from our own arrays.
type L = any; // eslint-disable-line @typescript-eslint/no-explicit-any
const HS_LAYERS = ["hs-fill", "hs-line", "hs-blocks"], HS_SOURCES = ["hs-precincts", "hs-blocks"];

/** Blocks as points: id (index into blocks), c (candidate index into cands), n. Largest first, so small ones draw on top. */
export function hotspotBlocks(data: SignersMap, only: string | null) {
  const blocks = data.blocks.filter((b) => !only || b.candidate === only).sort((x, y) => y.n - x.n);
  const cands = [...new Set(blocks.map((b) => b.candidate))];
  return { blocks, cands, max: Math.max(1, ...blocks.map((b) => b.n)), fc: { type: "FeatureCollection", features: blocks.map((b, id) => ({
    type: "Feature", geometry: { type: "Point", coordinates: [b.lon, b.lat] }, properties: { id, c: cands.indexOf(b.candidate), n: b.n, s: b.staged ? 1 : 0 } })) } };
}
/** The precinct choropleth: the candidate shown (the one picked, else the one with the most signers) and id/n per shape. */
export function hotspotPrecincts(data: SignersMap, only: string | null) {
  const tot = data.precincts.reduce<Record<string, number>>((a, p) => ({ ...a, [p.candidate]: (a[p.candidate] ?? 0) + p.n }), {});
  const pick = only ?? Object.entries(tot).sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
  const by = new Map(data.precincts.filter((p) => p.candidate === pick).map((p) => [`${p.ward}-${p.precinct}`, p.n]));
  const n = data.shapes.map((s) => by.get(`${s.ward}-${s.precinct}`) ?? 0);
  return { pick, n, max: Math.max(1, ...by.values()), fc: { type: "FeatureCollection", features: data.shapes.map((s, id) => ({ type: "Feature", geometry: s.geometry, properties: { id, n: n[id] } })) } };
}

function HotspotMap({ data, level, colorOf, only, lib, basemap, onRejected }:
  { data: SignersMap; level: "blocks" | "precincts"; colorOf: (c: string) => string; only: string | null; lib: GLib; basemap: Basemap; onRejected: () => void }) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L>(null);
  const popup = useRef<L>(null);
  const styleOf = useRef<string | null>(null);
  const fitted = useRef<unknown[]>([]);
  const hover = useRef<(layer: string, id: number) => string>(() => "");
  const [ready, setReady] = useState(0);   // bumps on every style load
  const styled = useRef(false);            // between setStyle and its style.load, layers can't be added
  const token = lib.token;
  useEffect(() => {
    const g = lib.g;
    if (!el.current || map.current) return;
    styleOf.current = token ? basemap : "open";
    const m = new g.Map({ container: el.current, style: styleFor(token, basemap), center: [-87.63, 41.88], zoom: 10, minZoom: 9, maxZoom: 19, scrollZoom: false,
      dragRotate: false, pitchWithRotate: false, collectResourceTiming: false, performanceMetricsCollection: false, attributionControl: true });
    m.addControl(new g.NavigationControl({ showCompass: false }), "bottom-right");
    m.on("style.load", () => { styled.current = true; setReady((n) => n + 1); });
    watchRejected(m, onRejected);
    popup.current = new g.Popup({ closeButton: false, closeOnClick: false, offset: 8 });
    for (const id of ["hs-blocks", "hs-fill"]) {
      m.on("mousemove", id, (e: L) => {
        const f = e.features?.[0];
        if (f) popup.current.setLngLat(e.lngLat).setText(hover.current(id, Number(f.properties.id))).addTo(m);
      });
      m.on("mouseleave", id, () => popup.current.remove());
    }
    map.current = m;
  }, [lib]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { map.current?.remove(); map.current = null; }, []);
  useEffect(() => {
    const m = map.current;
    if (!m || !token || styleOf.current === basemap) return;
    styleOf.current = basemap;
    styled.current = false;
    m.setStyle(BASEMAPS[basemap].style);
  }, [basemap, token, ready]);

  useEffect(() => {
    const m = map.current;
    if (!m || !ready || !styled.current) return;
    for (const id of HS_LAYERS) if (m.getLayer(id)) m.removeLayer(id);
    for (const id of HS_SOURCES) if (m.getSource(id)) m.removeSource(id);
    const dark = token ? BASEMAPS[basemap].dark : false;
    const outline = dark ? "#c8c8c6" : "#555555";
    const shapes = { type: "FeatureCollection", features: data.shapes.map((s) => ({ type: "Feature", geometry: s.geometry, properties: {} })) };
    let fit: { features: { geometry: { coordinates: unknown } }[] };
    if (level === "precincts") {
      const hp = hotspotPrecincts(data, only);
      m.addSource("hs-precincts", { type: "geojson", data: hp.fc });
      m.addLayer({ id: "hs-fill", type: "fill", source: "hs-precincts", paint: { "fill-color": colorOf(hp.pick ?? ""),
        "fill-opacity": ["+", 0.08, ["*", 0.72, ["/", ["get", "n"], hp.max]]] } });
      m.addLayer({ id: "hs-line", type: "line", source: "hs-precincts", paint: { "line-color": outline, "line-width": 1 } });
      hover.current = (_l, id) => { const s = data.shapes[id]; return s ? `Ward ${s.ward}, precinct ${s.precinct}: ${count(hp.n[id])} signers${hp.pick ? ` (${titleOf(hp.pick)})` : ""}` : ""; };
      fit = hp.fc as typeof fit;
    } else {
      const hb = hotspotBlocks(data, only);
      m.addSource("hs-precincts", { type: "geojson", data: shapes });
      m.addLayer({ id: "hs-line", type: "line", source: "hs-precincts", paint: { "line-color": dark ? "#9a9a98" : "#888888", "line-width": 0.6 } });
      const color = hb.cands.length ? ["match", ["get", "c"], ...hb.cands.flatMap((c, i) => [i, colorOf(c)]), COLORS[0]] : COLORS[0];
      m.addSource("hs-blocks", { type: "geojson", data: hb.fc });
      m.addLayer({ id: "hs-blocks", type: "circle", source: "hs-blocks", paint: {
        "circle-radius": ["+", 3, ["*", 11, ["sqrt", ["/", ["get", "n"], hb.max]]]], "circle-color": color, "circle-opacity": ["case", ["==", ["get", "s"], 1], 0.12, 0.45],
        "circle-stroke-color": ["case", ["==", ["get", "s"], 1], "#6250d6", color], "circle-stroke-width": ["case", ["==", ["get", "s"], 1], 1.5, 1] } });
      hover.current = (_l, id) => { const b = hb.blocks[id]; return b ? `${b.block}: ${count(b.n)} (${titleOf(b.candidate)})${b.staged ? ", not yet reviewed" : ""}` : ""; };
      fit = { features: [...hb.fc.features, ...shapes.features] } as typeof fit;
    }
    const key = [data, level, only];
    if (key.some((k, i) => k !== fitted.current[i])) {
      fitted.current = key;
      const b = bboxOf(fit);
      if (b) m.fitBounds(b, { padding: 12, duration: 0, maxZoom: 16 });
      else m.jumpTo({ center: [-87.63, 41.88], zoom: 11 });
    }
  }, [ready, data, level, only, colorOf, basemap, token]);
  return <div ref={el} className="signer-map__canvas signers-map" style={{ height: 460 }} role="img" aria-label="Map of petition signers by block or precinct" />;
}

const COUNT_HEADS: [CountKey, string][] = [["name", "Candidate"], ["office", "Office"], ["ward", "Ward"], ["sheets", "Sheets"], ["signed", "Signature lines"],
  ["struck", "Struck"], ["net", "Net"], ["required", "Minimum"], ["transcribed", "Transcribed"]];
const num = (n: number | null | undefined) => (n == null ? "—" : count(n));

function CountsTable() {
  const [data, setData] = useState<CountsPage | null>(null);
  const [failed, setFailed] = useState(false);
  const [sort, setSort] = useState<{ key: CountKey; dir: 1 | -1 }>({ key: "office", dir: 1 });
  useEffect(() => {
    fetch(`${API}/counts`, { credentials: "include", cache: "no-store" })
      .then((x) => (x.ok ? x.json() : Promise.reject())).then(setData).catch(() => setFailed(true));
  }, []);
  const rows = useMemo(() => (data ? sortCounts(data.rows, sort.key, sort.dir) : []), [data, sort]);
  const [reg, setReg] = useState(0);
  const validity = useValidity(reg);
  const est = useMemo(() => new Map(validity && validity !== "failed" ? validity.rows.map((e) => [e.candidate, e]) : []), [validity]);
  const [open, setOpen] = useState<string | null>(null);
  const openEst = open ? est.get(open) : undefined;
  const by = (key: CountKey) => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : key === "name" || key === "office" || key === "ward" ? 1 : -1 }));
  return (
    <section className="approvals-recent" aria-labelledby="signers-counts">
      <header style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <h2 className="section-title" id="signers-counts">Petition signature counts</h2>
        {data && <span className="access-sub">{count(data.totals.counted)} counted, {count(data.totals.pending)} pending</span>}
        {data && <a className="btn btn--secondary" href={`${API}/counts.csv`} download>Export counts CSV</a>}
      </header>
      <p className="access-sub">Signature lines: every signed line on the petition's sheets. Net: those lines less the crossed-out ones (the public pages' count). Minimum: the office's requirement where the repository has it. Likely valid: our estimate under the Board's rules, low to high; an estimate, not a Board ruling.</p>
      <ValidityControls reg={reg} setReg={setReg} data={validity} />
      {validity === "failed" && <p className="empty-panel">Couldn't load the estimates. Try again.</p>}
      {failed ? <p className="empty-panel">Couldn't load the counts. Try again.</p> : !data ? <p className="empty-panel">Loading…</p> : rows.length === 0 ? <p className="empty-panel">No counts loaded yet</p> : (
        <div className="art-table-wrap"><table className="art-table signers-counts">
          <thead><tr>{COUNT_HEADS.map(([k, label]) => (
            <th key={k} scope="col" aria-sort={sort.key === k ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
              <button type="button" onClick={() => by(k)}>{label}{sort.key === k ? (sort.dir === 1 ? " ▲" : " ▼") : ""}</button>
            </th>))}<th scope="col">Likely valid</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.candidate}>
              <th scope="row">{r.name}{r.status === "pending" && <> <span className="chip chip--neutral">Pending</span></>}</th>
              <td>{OFFICE[r.office] ?? (r.office === "alderperson" ? "Alderperson" : r.office)}</td>
              <td>{r.ward ?? (r.district ? `District ${r.district}` : "—")}</td>
              <td>{num(r.sheets)}</td><td>{num(r.signed)}</td><td>{num(r.struck)}</td>
              <td><b>{num(r.net)}</b></td>
              <td>{num(r.required)}</td>
              <td>{r.transcribed ? `${count(r.transcribed)}${r.signed ? ` (${pct(r.transcribed, r.signed)})` : ""}` : "—"}</td>
              <LikelyCell e={est.get(r.candidate)} open={open === r.candidate} onOpen={() => setOpen(open === r.candidate ? null : r.candidate)} />
            </tr>))}</tbody>
          <tfoot><tr>
            <th scope="row">Total ({count(data.totals.counted)} counted)</th><td /><td />
            <td>{count(data.totals.sheets)}</td><td>{count(data.totals.signed)}</td><td>{count(data.totals.struck)}</td>
            <td><b>{count(data.totals.net)}</b></td><td /><td>{count(data.totals.transcribed)}</td><td />
          </tr></tfoot>
        </table></div>
      )}
      {openEst && validity && validity !== "failed" && <ValidityDetail e={openEst} rules={validity.rules} reg={reg} onClose={() => setOpen(null)} />}
    </section>
  );
}

export interface OcrStatus {
  rows: { candidate: string; name: string; run: string; pages: number; queued: number; done: number; flagged: number; dead: number; msPerPage: number | null; tokensIn: number; tokensOut: number; updatedAt: string }[];
  totals: { pages: number; queued: number; done: number; flagged: number; dead: number; unchecked: number; tokensIn: number; tokensOut: number };
  reasons: Record<string, number>; stagedRows: Record<string, number>; notReviewed: number;
}
const REASON: Record<string, string> = { count_mismatch: "row count differs from the page count", unparsed: "no rows read", no_image: "image missing", error: "failed every retry" };

function OcrStatusTable() {
  const [data, setData] = useState<OcrStatus | null>(null);
  useEffect(() => {
    fetch(`${API}/ocr`, { credentials: "include", cache: "no-store" }).then((x) => (x.ok ? x.json() : Promise.reject())).then(setData).catch(() => setData(null));
  }, []);
  if (!data || data.rows.length === 0) return null;
  const t = data.totals;
  return (
    <section className="approvals-recent" aria-labelledby="signers-ocr">
      <header style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <h2 className="section-title" id="signers-ocr">OCR pipeline</h2>
        <span className="access-sub">{count(t.done)} done ({count(t.unchecked)} without a line count to check), {count(t.flagged)} flagged, {count(t.queued)} queued, {count(t.dead)} failed of {count(t.pages)} pages · {count(data.notReviewed)} rows not yet reviewed</span>
      </header>
      {Object.keys(data.reasons).length > 0 && <p className="access-sub">Flagged: {Object.entries(data.reasons).map(([k, n]) => `${count(n)} ${REASON[k] ?? k}`).join(", ")}. Staged rows wait for review before they join the signer list.</p>}
      <div className="art-table-wrap"><table className="art-table signers-ocr">
        <thead><tr><th scope="col">Candidate</th><th scope="col">Run</th><th scope="col">Pages</th><th scope="col">Queued</th><th scope="col">Done</th><th scope="col">Flagged</th><th scope="col">Failed</th><th scope="col">Seconds a page</th></tr></thead>
        <tbody>{data.rows.map((r) => (
          <tr key={r.candidate}><th scope="row">{r.name}</th><td>{r.run}</td><td>{count(r.pages)}</td><td>{count(r.queued)}</td><td>{count(r.done)}</td><td>{count(r.flagged)}</td><td>{count(r.dead)}</td>
            <td>{r.msPerPage == null ? "—" : (r.msPerPage / 1000).toFixed(1)}</td></tr>))}</tbody>
        <tfoot><tr><th scope="row">Total</th><td /><td>{count(t.pages)}</td><td>{count(t.queued)}</td><td>{count(t.done)}</td><td>{count(t.flagged)}</td><td>{count(t.dead)}</td><td /></tr></tfoot>
      </table></div>
    </section>
  );
}

export interface DriveStatus {
  listing: "api" | "folder_page" | "none" | null;
  lastRun: { at: string; listing: string; files: number; new: number; updated: number; started: number; error: string | null } | null;
  files: { seen: number; known: number; new: number; updated: number; baseline: number; downloaded: number; petitions: number; notPetitions: number; unmatched: number; errors: number; byStatus: Record<string, number> };
  pages: { inFiles: number; rendered: number; ocr: { pages: number; queued: number; done: number; flagged: number; dead: number; unchecked: number } };
  needsAttention: { name: string; version: number; office: string | null; ward: number | null; district: number | null; match: string; slug: string | null; status: string; pages: number | null; rendered: number | null; error: string | null; updatedAt: string }[];
}
const DRIVE_STATUS: Record<string, string> = { error: "error", held: "waiting for tomorrow's OCR budget", queued: "sent to the OCR", rendered: "rendered", rendering: "rendering", started: "starting",
  downloading: "downloading", downloaded: "downloaded", new: "new", skipped: "not a petition", baseline: "in R2 before the pipeline" };

function DrivePipeline() {
  const [data, setData] = useState<DriveStatus | null>(null);
  useEffect(() => {
    fetch(`${API}/drive`, { credentials: "include", cache: "no-store" }).then((x) => (x.ok ? x.json() : Promise.reject())).then(setData).catch(() => setData(null));
  }, []);
  if (!data) return null;
  const f = data.files, pg = data.pages;
  return (
    <section className="approvals-recent" aria-labelledby="signers-drive">
      <header style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <h2 className="section-title" id="signers-drive">Drive pipeline</h2>
        <span className={`chip ${data.listing === "api" ? "chip--positive" : "chip--neutral"}`}>{data.listing === "api" ? "Drive API" : data.listing === "folder_page" ? "Drive API key not set" : "Not run yet"}</span>
      </header>
      <p className="access-sub">
        {data.listing === "api" ? "The folder is listed through the Drive API (checksums catch amended uploads)." : "Without the pipeline Worker's GOOGLE_DRIVE_API_KEY the folder is listed from its public page (no checksums)."}
        {" Files download by their public links, every 15 minutes."}
        {data.lastRun && ` Last check ${new Date(data.lastRun.at).toLocaleString("en-US")}${data.lastRun.error ? `: ${data.lastRun.error}` : ""}.`}
      </p>
      <div className="art-table-wrap"><table className="art-table signers-drive">
        <tbody>
          <tr><th scope="row">Files in the folder</th><td>{count(f.seen)}</td></tr>
          <tr><th scope="row">New (since the pipeline started) / updated versions</th><td>{count(f.new)} / {count(f.updated)}</td></tr>
          <tr><th scope="row">In R2 before the pipeline (rendered on staff machines)</th><td>{count(f.baseline)}</td></tr>
          <tr><th scope="row">Downloaded by the pipeline</th><td>{count(f.downloaded)}</td></tr>
          <tr><th scope="row">Pages rendered / in its files</th><td>{count(pg.rendered)} / {count(pg.inFiles)}</td></tr>
          <tr><th scope="row">Pages sent to the OCR: queued, done, flagged, failed</th><td>{count(pg.ocr.queued)}, {count(pg.ocr.done)} ({count(pg.ocr.unchecked)} unchecked), {count(pg.ocr.flagged)}, {count(pg.ocr.dead)}</td></tr>
          <tr><th scope="row">Files a person must map to a candidate</th><td>{count(f.unmatched)}</td></tr>
          <tr><th scope="row">Errors</th><td>{count(f.errors)}</td></tr>
        </tbody>
      </table></div>
      {data.needsAttention.length > 0 && <>
        <p className="access-sub">Unmatched files keep a provisional slug (file-…) and are never shown on the public pages; counts and filings are published only after review.</p>
        <div className="art-table-wrap"><table className="art-table signers-drive-files">
          <thead><tr><th scope="col">File</th><th scope="col">Office</th><th scope="col">Match</th><th scope="col">Pages</th><th scope="col">Status</th></tr></thead>
          <tbody>{data.needsAttention.map((r) => (
            <tr key={`${r.name}-${r.version}`}><th scope="row">{r.name}{r.version > 1 ? ` (v${r.version})` : ""}</th>
              <td>{(r.office ?? "—").replace(/_/g, " ")}{r.ward ? ` ${r.ward}` : r.district ? ` ${r.district}` : ""}</td><td>{r.match}</td>
              <td>{r.pages == null ? "—" : `${count(r.rendered ?? 0)} / ${count(r.pages)}`}</td><td>{DRIVE_STATUS[r.status] ?? r.status}{r.error ? `: ${r.error}` : ""}</td></tr>))}</tbody>
        </table></div>
      </>}
    </section>
  );
}

interface GeoStatus {
  totals: { rows: number; geocoded: number; pending: number; matched: number; exact: number; fuzzy: number; unmatched: number; inside: number; outside: number };
  aldermanic: { rows: number; inside: number; outside: number; insideShare: number | null };
  keys: { total: number; mapped: number; unmatched: { key: string; reason: string | null }[] };
  candidates: { slug: string; name: string | null; office: string; ward: number | null; district: number | null; keys: number; rows: number; geocoded: number; exact: number;
    fuzzy: number; none: number; inside: number; outside: number; unmatched: boolean }[];
  index: { points: number }; runs: { status: string; updated: string; rows: number }[];
}
/** The status line: OCR rows geocoded, matched, unmatched; keys mapped; then per candidate (counts only). */
export function geoLine(g: GeoStatus): string {
  const t = g.totals;
  return `${count(t.rows)} OCR rows: ${count(t.geocoded)} geocoded (${pct(t.geocoded, t.rows)}), ${count(t.matched)} matched (${count(t.exact)} exact, ${count(t.fuzzy)} fuzzy), `
    + `${count(t.unmatched)} unmatched, ${count(t.pending)} pending. Petition files: ${count(g.keys.mapped)} of ${count(g.keys.total)} mapped to a candidate.`;
}
function GeoStatusTable() {
  const [g, setG] = useState<GeoStatus | null>(null);
  useEffect(() => {
    let live = true;
    const load = () => fetch(`${API}/geo`, { credentials: "include", cache: "no-store" }).then((x) => (x.ok ? x.json() : Promise.reject())).then((v) => live && setG(v)).catch(() => {});
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 60_000);
    return () => { live = false; clearInterval(t); };
  }, []);
  if (!g) return null;
  return (
    <section className="approvals-recent" aria-labelledby="signers-geo">
      <h2 className="section-title" id="signers-geo">Not yet reviewed lines on the map</h2>
      <p className="access-sub" role="status">{geoLine(g)}{g.aldermanic.insideShare != null ? ` Aldermanic petitions: ${pct(g.aldermanic.inside, g.aldermanic.inside + g.aldermanic.outside)} of geocoded lines in the petition's ward.` : ""} Address index: {count(g.index.points)} Cook County address points; nothing is sent to an outside geocoder.</p>
      {g.keys.unmatched.length > 0 && <p className="access-sub">Files not matched to a candidate: {g.keys.unmatched.map((u) => u.key).join(", ")}</p>}
      <details><summary>Per candidate ({count(g.candidates.length)})</summary>
        <div className="art-table-wrap"><table className="art-table signers-geo">
          <thead><tr><th scope="col">Candidate</th><th scope="col">Race</th><th scope="col">Lines</th><th scope="col">Geocoded</th><th scope="col">Exact</th><th scope="col">Fuzzy</th><th scope="col">No match</th><th scope="col">In area</th><th scope="col">Outside</th></tr></thead>
          <tbody>{g.candidates.map((c) => (
            <tr key={c.slug}><td><a className="link" href={`/dashboard/admin/signers/map?candidate=${encodeURIComponent(c.slug)}`}>{c.name ?? titleOf(c.slug)}</a>{c.unmatched ? " (file not matched)" : ""}</td><td>{raceOf(c)}</td>
              <td>{count(c.rows)}</td><td>{pct(c.geocoded, c.rows)}</td><td>{count(c.exact)}</td><td>{count(c.fuzzy)}</td><td>{count(c.none)}</td><td>{c.office === "alderperson" || c.office === "police_district_council" ? count(c.inside) : "—"}</td><td>{c.office === "alderperson" || c.office === "police_district_council" ? count(c.outside) : "—"}</td></tr>
          ))}</tbody>
        </table></div>
      </details>
    </section>
  );
}

export function AdminSigners() {
  const [page, setPage] = useState<SignersPage | null>(null);
  const [mapData, setMapData] = useState<SignersMap | null>(null);
  const [failed, setFailed] = useState(false);
  const [candidate, setCandidate] = useState<string | null>(null);
  const [ward, setWard] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [level, setLevel] = useState<"blocks" | "precincts">("blocks");
  const [showStaged, setShowStaged] = useState(true);
  const [basemap, setBasemap] = useState<Basemap>("light");
  const [rejected, setRejected] = useState(false);
  const onRejected = useMemo(() => () => setRejected(true), []);
  const cfg = useMapConfig();
  const lib = useGL(cfg);
  useEffect(() => { document.title = "Petition signers · Chicago Politics"; }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      fetch(`${API}${signerQuery({ candidate, ward, q, offset })}`, { credentials: "include", cache: "no-store" })
        .then((x) => (x.ok ? x.json() : Promise.reject())).then((p: SignersPage) => { setPage(p); setFailed(false); }).catch(() => setFailed(true));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [candidate, ward, q, offset]);
  useEffect(() => {
    fetch(`${API}/map${signerQuery({ candidate, ward })}`, { credentials: "include", cache: "no-store" })
      .then((x) => (x.ok ? x.json() : Promise.reject())).then(setMapData).catch(() => setMapData(null));
  }, [candidate, ward]);

  const cands = page?.candidates ?? [];
  const staged = page?.staged ?? [];
  // The dropdown: every candidate with lines, reviewed or not yet reviewed.
  const options = useMemo(() => {
    const m = new Map<string, { slug: string; name: string | null; office: string; ward: number | null; district: number | null; reviewed: number; staged: number }>();
    for (const c of cands) m.set(c.slug, { slug: c.slug, name: null, office: c.office, ward: c.ward, district: c.district, reviewed: c.rows, staged: 0 });
    for (const c of staged) { const cur = m.get(c.slug); if (cur) cur.staged = c.rows; else m.set(c.slug, { slug: c.slug, name: c.name, office: c.office, ward: c.ward, district: c.district, reviewed: 0, staged: c.rows }); }
    return [...m.values()];
  }, [cands, staged]);
  const wards = useMemo(() => [...new Set(options.map((c) => c.ward).filter((w): w is number => w != null))].sort((a, b) => a - b), [options]);
  const order = useMemo(() => [...new Set(options.map((c) => c.slug))], [options]);
  const colorOf = useMemo(() => (slug: string) => COLORS[Math.max(0, order.indexOf(slug)) % COLORS.length], [order]);
  const hotspotData = useMemo(() => (mapData ? withStaged(mapData, showStaged) : null), [mapData, showStaged]);
  const shown = cands.filter((c) => (!candidate || c.slug === candidate) && (!ward || c.ward === ward));

  return (
    <div className="access-page admin-page">
      <header className="access-head">
        <h1 className="page-title">Petition signers</h1>
        <span className="chip chip--neutral">Staff only</span>
        <a className="btn btn--secondary" href="/dashboard/admin/signers/map">Signer map</a>
      </header>
      <p className="access-sub">Names and addresses from nominating petitions filed with the Board of Election Commissioners, for internal analysis. Never published. The map shows blocks and precincts, never an address.</p>

      <div className="signers-filters" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <label>Candidate{" "}
          <select value={candidate ?? ""} onChange={(e) => { setCandidate(e.target.value || null); setOffset(0); }}>
            <option value="">All</option>
            {options.filter((c) => !ward || c.ward === ward).map((c) => <option key={c.slug} value={c.slug}>{c.name ?? titleOf(c.slug)} ({raceOf(c)}){c.reviewed ? "" : ", not yet reviewed"}</option>)}
          </select>
        </label>
        <label>Ward{" "}
          <select value={ward ?? ""} onChange={(e) => { setWard(e.target.value ? Number(e.target.value) : null); setOffset(0); }}>
            <option value="">All</option>
            {wards.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </label>
        <input type="search" placeholder="Search name or address" value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} aria-label="Search name or address" />
        {page && (page.canExport
          ? <a className="btn btn--secondary" href={`${API}/export.csv${signerQuery({ candidate, ward, q })}`} download>Export CSV</a>
          : <span className="access-sub">Export is for staff owners and admins.</span>)}
      </div>

      {failed ? <p className="empty-panel">Couldn't load petition signers. Try again.</p> : !page ? <p className="empty-panel">Loading…</p> : <>
        <div className="access-cards admin-analysis">
          {shown.length === 0 && !((candidate || ward) && staged.some((c) => (!candidate || c.slug === candidate) && (!ward || c.ward === ward))) ? <p className="empty-panel">No petitions loaded for this filter yet</p> : shown.map((c) => (
            <section key={c.slug} className="access-card" aria-label={titleOf(c.slug)}>
              <h2><span aria-hidden style={{ display: "inline-block", width: 10, height: 10, borderRadius: 5, background: colorOf(c.slug), marginRight: 6 }} />{titleOf(c.slug)}</h2>
              <div className="flags-count"><b>{count(c.signed)}</b><span>signers ({raceOf(c)})</span></div>
              <div className="access-rule"><span>Lines read</span><b>{count(c.rows)}</b></div>
              <div className="access-rule"><span>Addresses matched</span><b>{pct(c.matched, c.rows)}</b></div>
            </section>
          ))}
          {(candidate || ward) && staged.filter((c) => (!candidate || c.slug === candidate) && (!ward || c.ward === ward)).map((c) => (
            <section key={`s-${c.slug}`} className="access-card" aria-label={`${c.name ?? titleOf(c.slug)}, not yet reviewed`}>
              <h2><span aria-hidden style={{ display: "inline-block", width: 10, height: 10, borderRadius: 5, border: "2px solid #6250d6", marginRight: 6 }} />{c.name ?? titleOf(c.slug)}</h2>
              <div className="flags-count"><b>{count(c.signed)}</b><span>lines not yet reviewed ({raceOf(c)})</span></div>
              <div className="access-rule"><span>Geocoded</span><b>{pct(c.geocoded, c.rows)}</b></div>
              {(c.office === "alderperson" || c.office === "police_district_council") && <div className="access-rule"><span>In the {c.office === "alderperson" ? "ward" : "district"}</span><b>{pct(c.inside, c.inside + c.outside)}</b></div>}
            </section>
          ))}
        </div>

        <CountsTable />
        <OcrStatusTable />
        <DrivePipeline />
        <GeoStatusTable />

        <section className="approvals-recent" aria-labelledby="signers-map">
          <header style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <h2 className="section-title" id="signers-map">Hotspots</h2>
            <div role="radiogroup" aria-label="Map level" style={{ display: "flex", gap: 6 }}>
              {(["blocks", "precincts"] as const).map((l) => (
                <button key={l} type="button" role="radio" aria-checked={level === l} className={`chip ${level === l ? "chip--positive" : "chip--neutral"}`} onClick={() => setLevel(l)}>
                  {l === "blocks" ? "Blocks" : "Precincts"}
                </button>
              ))}
            </div>
            {mapData?.staged && (mapData.staged.needsFilter
              ? <span className="access-sub">Pick a candidate or ward to add {count(mapData.staged.rows)} lines not yet reviewed.</span>
              : <button type="button" className={`chip ${showStaged ? "chip--positive" : "chip--neutral"}`} aria-pressed={showStaged} onClick={() => setShowStaged(!showStaged)}>Not yet reviewed ({count(mapData.staged.rows)})</button>)}
            {level === "precincts" && !candidate && <span className="access-sub">Showing the candidate with the most signers; pick one above.</span>}
            <a className="link" style={{ marginLeft: "auto" }} href={`/dashboard/admin/signers/map${candidate ? `?candidate=${encodeURIComponent(candidate)}` : ""}`}>Open the full signer map (points, clusters, heatmap, search)</a>
          </header>
          {lib === "failed" ? <p className="empty-panel">The map could not load.</p> : mapData && lib ? (
            <div className="signer-map__stage">
              <HotspotMap data={hotspotData!} level={level} colorOf={colorOf} only={candidate} lib={lib} basemap={basemap} onRejected={onRejected} />
              <div className="signer-map__switch" role="group" aria-label="Map views">
                <BasemapSwitch value={basemap} onChange={setBasemap} enabled={!!lib.token} />
              </div>
              <MapNotice cfg={cfg} lib={lib} rejected={rejected} />
            </div>
          ) : <p className="empty-panel">Loading the map…</p>}
        </section>

        <section className="approvals-recent" aria-labelledby="signers-list">
          <h2 className="section-title" id="signers-list">Signers <span className="access-sub">{count(page.total)}</span></h2>
          {page.rows.length === 0 ? <p className="empty-panel">None</p> : (
            <List as="div" className="members admin-analysis-errors" role="table" aria-labelledby="signers-list">
              <ListRow as="div" className="members__head ls-head" role="row"><Head width={130}>Candidate</Head><Head width={80}>Sheet</Head><Head min={180}>Name</Head><Head min={220}>Address</Head><Head width={80}>Precinct</Head><Head width={100}>Legibility</Head></ListRow>
              {page.rows.map((r) => (
                <ListRow key={r.id} as="div" className="members__row" role="row">
                  <Cell width={130} className="members__active">{titleOf(r.candidate)}</Cell>
                  <Cell width={80} className="members__active">{r.sheet ?? "?"}:{r.line}</Cell>
                  <Cell min={180} className="approvals__org"><RowTitle as="b">{r.name || "(unreadable)"}</RowTitle>{r.struck && <RowSub>Struck</RowSub>}</Cell>
                  <Cell min={220} className="approvals__org"><RowTitle as="span">{r.address || "(unreadable)"}</RowTitle><RowSub>{r.match === "none" ? "Not matched" : `${r.normalized ?? ""}${r.match === "block" ? " (block)" : ""}`}</RowSub></Cell>
                  <Cell width={80} className="members__active">{r.precinct ?? ""}</Cell>
                  <Cell width={100} className="members__active">{r.legibility}</Cell>
                </ListRow>
              ))}
            </List>
          )}
          {page.total > page.pageSize && (
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button type="button" className="btn btn--secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - page.pageSize))}>Previous</button>
              <span className="access-sub">{count(offset + 1)}–{count(Math.min(page.total, offset + page.pageSize))} of {count(page.total)}</span>
              <button type="button" className="btn btn--secondary" disabled={offset + page.pageSize >= page.total} onClick={() => setOffset(offset + page.pageSize)}>Next</button>
            </div>
          )}
        </section>
      </>}
    </div>
  );
}
